import React, { useEffect, useState } from 'react'
import { useCreateUser } from '@/hooks/useUsers'

export interface CreateStudentModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

export const CreateStudentModal: React.FC<CreateStudentModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [phone, setPhone] = useState('')
  const [error, setError] = useState<string | null>(null)

  const createUser = useCreateUser()

  useEffect(() => {
    if (!isOpen) return
    setName('')
    setEmail('')
    setPassword('')
    setPhone('')
    setError(null)
  }, [isOpen])

  const validate = (): string | null => {
    if (!name.trim()) return 'Nama wajib diisi'
    if (!email.trim() || !email.includes('@')) return 'Email tidak valid'
    if (password.length < 8) return 'Password minimal 8 karakter'
    return null
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const msg = validate()
    if (msg) {
      setError(msg)
      return
    }
    setError(null)
    try {
      await createUser.mutateAsync({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        password,
        phone: phone.trim() || undefined,
        role: 'student',
      })
      onSuccess?.()
      onClose()
    } catch (err) {
      const e = err as Error
      setError(e.message || 'Gagal menyimpan')
    }
  }

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white dark:bg-surface-card border border-gray-200 dark:border-white/10 rounded-lg w-full max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-white/10">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Tambah Peserta</h2>
          <button
            type="button"
            onClick={onClose}
            className="size-8 rounded-lg bg-gray-100 dark:bg-white/5 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-white/10"
          >
            <span className="material-symbols-outlined text-[18px]">close</span>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Nama lengkap
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-background-light dark:bg-background-dark border border-gray-200 dark:border-white/10 rounded text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
              autoComplete="new-password"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2 bg-background-light dark:bg-background-dark border border-gray-200 dark:border-white/10 rounded text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
              autoComplete="new-password"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Password (minimal 8 karakter)
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full px-3 py-2 bg-background-light dark:bg-background-dark border border-gray-200 dark:border-white/10 rounded text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
              autoComplete="new-password"
              required
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-1">
              Nomor HP (opsional)
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              className="w-full px-3 py-2 bg-background-light dark:bg-background-dark border border-gray-200 dark:border-white/10 rounded text-gray-900 dark:text-white focus:outline-none focus:border-primary/50"
              placeholder="08xxxxxxxxxx"
            />
          </div>

          {error && (
            <div role="alert" className="p-2 text-sm rounded bg-red-500/10 text-red-300">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-2 border-t border-gray-200 dark:border-white/10">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-white/5 hover:bg-gray-200 dark:hover:bg-white/10"
            >
              Batal
            </button>
            <button
              type="submit"
              disabled={createUser.isPending}
              className="px-4 py-2 rounded-lg bg-primary text-white font-semibold disabled:opacity-50"
            >
              {createUser.isPending ? 'Menyimpan...' : 'Simpan'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateStudentModal
